import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { CDialogProps } from 'types/molecules';
import { useTranslation } from 'react-i18next';
import { COutlinedButton, CContainedButton } from 'components/atoms';
import Draggable from 'react-draggable';
import { ReactComponent as CloseSquareSvg } from 'assets/svg/close-square.svg';
import { ReactComponent as ExpandSquareSvg } from 'assets/svg/expand-square.svg';
import { ReactComponent as ShrinkSquareSvg } from 'assets/svg/shrink-square.svg';
import { ReactComponent as ShrinkSquare2Svg } from 'assets/svg/shrink-square-2.svg';
import { ReactComponent as FullScreenSquareSvg } from 'assets/svg/full-screen-square.svg';
import customTwMerge from 'utils/twMerge';
import { useEffect, useState } from 'react';

export default function CDialog({
  open,
  isDraggable,
  isFullScreen,
  isCentred,
  onApprove,
  onApproveTitle,
  onClose,
  onCloseTitle,
  title,
  message,
  actions,
  children,
  enableBackdropClick,
  type,
  jitsiRef,
}: CDialogProps) {
  const { t } = useTranslation('common');
  const initialPosition = { x: 0, y: 0 };
  const [position, setPosition] = useState(initialPosition);

  const types = {
    warning: {
      src: new URL('assets/svg/warning-dialog.svg', import.meta.url).href,
      title: t('warning'),
    },
    error: {
      src: new URL('assets/svg/error.svg', import.meta.url).href,
      title: t('error'),
    },
    success: {
      src: new URL('assets/svg/success.svg', import.meta.url).href,
      title: t('success'),
    },
  };

  const iconType = type && types[type];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = isDraggable ? 'scroll' : 'hidden';

      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isDraggable]);

  return open ? (
    <Draggable
      position={position}
      onDrag={(e, data) => setPosition({ x: data.x, y: data.y })}
      disabled={!isDraggable}
      bounds="body"
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"], .dialog-content'}
      defaultClassName={customTwMerge(
        'fixed bottom-0 right-0 z-50',
        isDraggable
          ? 'h-min w-min top-[unset] left-[unset]'
          : 'top-0 left-0 !translate-x-0 !translate-y-0'
      )}
    >
      <Dialog
        open={open}
        onClose={enableBackdropClick ? onClose : undefined}
        aria-labelledby={`dialog-${title}`}
        aria-describedby={`dialog-${message}`}
        classes={{
          paper: customTwMerge(
            'flex flex-col rounded-xl text-center m-2 max-w-none',
            isFullScreen ? 'h-[100dvh] m-0' : '',
            isDraggable ? 'aspect-square' : '',
            isCentred ? 'aspect-auto h-[90dvh] w-[90vw]' : ''
          ),
        }}
        disablePortal
        disableScrollLock={isDraggable} // NOTE: not working dynamically. We control it with useEffect.
        fullScreen={isFullScreen}
        hideBackdrop={isDraggable}
        disableEnforceFocus={isDraggable}
      >
        <>
          {title && (
            <div className="flex gap-2 whitespace-nowrap bg-primary-0 px-2 py-1">
              <DialogTitle
                className={`flex-1 p-0 text-label20 text-neutral-700 ${
                  isDraggable ? 'cursor-move' : ''
                }`}
                id={isDraggable ? 'draggable-dialog-title' : 'dialog-title'}
              >
                {title}
              </DialogTitle>

              {jitsiRef && (
                <div className="flex gap-2">
                  <button
                    className="hover-transition-xs fill-transparent hover:text-primary-600"
                    onClick={() => {
                      jitsiRef?.minimize && jitsiRef.minimize();
                      if (isCentred) {
                        setPosition(initialPosition);
                      }
                    }}
                  >
                    {isDraggable ? (
                      <ExpandSquareSvg className="fill-inherit text-inherit" />
                    ) : (
                      <ShrinkSquareSvg className="fill-inherit text-inherit" />
                    )}
                  </button>

                  <button
                    className="hover-transition-xs fill-transparent hover:text-primary-600"
                    onClick={() => {
                      jitsiRef?.fullScreen && jitsiRef.fullScreen();
                      setPosition(initialPosition);
                    }}
                  >
                    {isFullScreen ? (
                      <ShrinkSquare2Svg className="fill-inherit text-inherit" />
                    ) : (
                      <FullScreenSquareSvg className="fill-inherit text-inherit" />
                    )}
                  </button>

                  <button
                    className="hover-transition-xs fill-transparent hover:text-primary-600"
                    onClick={jitsiRef?.close}
                  >
                    <CloseSquareSvg className="fill-inherit text-danger" />
                  </button>
                </div>
              )}
            </div>
          )}

          <div
            className={`dialog-content h-full p-0 md:p-4 ${title ? '!pt-0' : ''} ${
              isCentred ? 'w-full' : ''
            }`}
          >
            {iconType && <img className="w-20 self-center" src={iconType.src} alt={iconType.title} />}

            {message && (
              <DialogContent className="p-0">
                <DialogContentText className="text-label16 text-neutral-600" id="dialog-message">
                  {message}
                </DialogContentText>
              </DialogContent>
            )}

            {(onApprove || onClose || actions) && (
              <DialogActions className="justify-center gap-2 p-0 md:gap-5">
                {onClose && (
                  <COutlinedButton className="flex-1" onClick={onClose}>
                    {onCloseTitle ?? t('close')}
                  </COutlinedButton>
                )}

                {onApprove && (
                  <CContainedButton className="!ml-0 flex-1" onClick={onApprove}>
                    {onApproveTitle ?? t('confirm')}
                  </CContainedButton>
                )}

                {actions}
              </DialogActions>
            )}

            {children}
          </div>
        </>
      </Dialog>
    </Draggable>
  ) : null;
}

